import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react'
import { Event, initialize, VariationValue } from '@harnessio/ff-javascript-client-sdk'
import { sha256 } from 'js-sha256'
import { useAuth0 } from '@auth0/auth0-react'
import { Environment, getEnvironment } from '../config'

interface FeatureFlagProps {
  children: ReactNode
}

interface FeatureFlagContextType {
  featureFlags: Record<string, VariationValue>
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({} as FeatureFlagContextType)

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext)
  return context?.featureFlags
}

export const FeatureFlagProvider: FC<FeatureFlagProps> = ({ children }) => {
  const { user } = useAuth0()
  const [featureFlags, setFeatureFlags] = useState({} as Record<string, VariationValue>)

  useEffect(() => {
    const key =
      getEnvironment() === Environment.DEV || getEnvironment() === Environment.LOCAL
        ? 'abdbd384-f790-4bbf-9292-dac2f8a602d3'
        : getEnvironment() === Environment.STAGING
          ? '4f74c9ab-f9df-42df-bc16-deb9b3355d3e'
          : getEnvironment() === Environment.PRODUCTION
            ? 'db08086d-79e2-42d6-a7dc-514c4263dd97'
            : ''

    const hashedEmail = sha256(user?.email?.toLowerCase() || 'unknown')

    const cf = initialize(
      key,
      {
        identifier: hashedEmail,
        attributes: {
          lastUpdated: Date(),
          host: window.location.href,
        },
      },
      {
        baseUrl: 'https://config.ff.harness.io/api/1.0',
        eventUrl: 'https://events.ff.harness.io/api/1.0',
      }
    )
    cf.on(Event.READY, (flags) => {
      setFeatureFlags(flags)
    })

    cf.on(Event.CHANGED, (flagInfo) => {
      if (flagInfo.deleted) {
        setFeatureFlags((currentFeatureFlags: Record<string, VariationValue>) => {
          delete currentFeatureFlags[flagInfo.flag]
          return { ...currentFeatureFlags }
        })
      } else {
        setFeatureFlags((currentFeatureFlags: Record<string, VariationValue>) => ({
          ...currentFeatureFlags,
          [flagInfo.flag]: flagInfo.value,
        }))
      }
    })

    return () => {
      cf?.close()
    }
  }, [user])

  return <FeatureFlagContext.Provider value={{ featureFlags }}>{children}</FeatureFlagContext.Provider>
}

export default FeatureFlagContext
